import { useModal } from "@/Contexts";
import { DeleteIcon } from "@/Icons/v2/Delete";
import { DownloadIcon } from "@/Icons/v2/Download";
import { EyeIcon } from "@/Icons/v2/Eye";
import { MoveIcon } from "@/Icons/v2/Move";
import { RenameIcon } from "@/Icons/v2/Rename";
import { ThreeDotsIcon } from "@/Icons/v2/ThreeDots";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import {
  FileStatus,
  type LibraryFile,
} from "@/pages/library_v2/components/filesView/types";
import {
  downloadFile,
  getIsPreviewable,
} from "@/pages/library_v2/components/filesView/utils";
import { useTrans } from "@/translations";

import { type RefObject, useCallback, useContext, useMemo } from "react";

import { Popover, PopoverPanel } from "@headlessui/react";
import type { FILE_LOCATION_TYPE } from "server/bucketApi/library/constants.js";

import { PreviewFileModal } from "../previewFileModal";
import { ActionButton } from "./actionButton";
import { DeleteFileModal } from "./deleteFileModal";

const menuItemClass =
  "hover:bg-grey1 flex w-full cursor-pointer items-center gap-[9px] rounded-[5px] px-[10px] py-2 text-left text-[12.5px] text-[#1D1C1C]";

type Props = {
  file: LibraryFile;
  tooltipPlacement: "bottom" | "top";
  moveButtonRef: RefObject<HTMLButtonElement | null>;
  downloadButtonRef: RefObject<HTMLButtonElement | null>;
  setIsRenaming: (value: boolean) => void;
};

export const ExtraFileActions: React.FC<Props> = ({
  file,
  tooltipPlacement,
  moveButtonRef,
  downloadButtonRef,
  setIsRenaming,
}) => {
  const { t } = useTrans();
  const { setGlobalAlert } = useGlobalAlert();
  const { currentTerminal } = useTerminalsState();
  const { showModal, hideModal } = useModal();
  const { refetchFiles } = useContext(LibraryPageContext);

  const isPreviewableFile = useMemo(() => getIsPreviewable(file), [file]);

  const { mutate: deleteFile, isPending: isDeleting } =
    trpc.library.deleteFile.useMutation({
      onSuccess: () => {
        refetchFiles();
        hideModal();

        setGlobalAlert({
          type: "success",
          message: "library:fileDeleteSuccessful",
        });
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:documentDeleteFailed",
        });
        console.error("Error deleting document:", error);
      },
    });

  const onDeleteFile = useCallback(
    async (_fileId: string) => {
      showModal(
        <DeleteFileModal
          filename={file.name}
          isLoading={isDeleting}
          onConfirm={() => {
            deleteFile({
              terminalId: currentTerminal?.id!,
              filename: `${file.name}.${file.format}`,
              folderName: file.folderName,
              type: file.status as string as FILE_LOCATION_TYPE,
            });
          }}
          onCancel={hideModal}
        />,
      );
    },
    [currentTerminal?.id, deleteFile, file, hideModal, isDeleting, showModal],
  );

  return (
    <Popover>
      {({ open }) => (
        <>
          <ActionButton
            icon={<ThreeDotsIcon size={20} color="#1D1C1C" />}
            label={t("libraryPage:fileListActionMore")}
            align="right"
            placement={tooltipPlacement}
            alwaysVisible
            popover
            tooltipHidden={open}
          />

          <PopoverPanel
            anchor={{ to: "bottom end", gap: 6 }}
            className="border-grey3 z-[10000] w-[196px] rounded-lg border bg-white p-[5px] shadow-lg"
          >
            {({ close }) => (
              <>
                {isPreviewableFile && (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      showModal(
                        <PreviewFileModal
                          file={file}
                          onDownload={downloadFile}
                        />,
                      );
                    }}
                    className={menuItemClass}
                  >
                    <EyeIcon size={16} />
                    {t("libraryPage:fileListActionPreview")}
                  </button>
                )}
                {file.status === FileStatus.PUBLISHED && (
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      moveButtonRef.current?.click();
                    }}
                    className={menuItemClass}
                  >
                    <MoveIcon size={16} />
                    {t("libraryPage:fileListActionMoveToFolder")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    close();
                    downloadButtonRef.current?.click();
                  }}
                  className={menuItemClass}
                >
                  <DownloadIcon size={16} />
                  {t("libraryPage:fileListActionDownload")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    setIsRenaming(true);
                  }}
                  className={menuItemClass}
                >
                  <RenameIcon size={16} />
                  {t("libraryPage:fileListActionRename")}
                </button>
                <div className="my-1 h-px bg-[#F1EAEA]" />
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onDeleteFile(file.id);
                  }}
                  className="flex w-full cursor-pointer items-center gap-[9px] rounded-[5px] px-[10px] py-2 text-left text-[12.5px] text-[#B73A23] hover:bg-[#FBEDE9]"
                >
                  <DeleteIcon />
                  {t("libraryPage:fileListActionDelete")}
                </button>
              </>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
